import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Config, Minus, Plus } from "@mynaui/icons-react";
import { Button, Checkbox, Dialog, Tag } from "../../ui";
import "./styles.scss";

interface FilterDialogProps {
  filters?: Filter[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: Filter[]) => void;
}

export type Filter = {
  label: string;
  checkbox?: boolean;
  radio?: boolean;
  collapsed?: boolean;
  showMore?: boolean;
  options?: { label: string; value: string; selected?: boolean }[];
};

/**
 * The "Filter" button that opens the FilterDialog, with the number of filters applied. Shared by the list pages
 * (players, representers, tournaments, a category's players / teams).
 */
export const FilterButton = ({ count, onClick }: { count: number; onClick: () => void }) => (
  <Button variant="ghost" icon={<Config size={18} />} onClick={onClick}>
    Filter
    {count > 0 && <Tag tone="solid-spark">{count}</Tag>}
  </Button>
);

const FilterDialog: React.FC<FilterDialogProps> = ({ isOpen, onClose, onApply, filters: dialogFilters }) => {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [isReset, setIsReset] = useState(false);

  useEffect(() => {
    if (dialogFilters)
      setFilters(
        dialogFilters?.map((item) => ({
          ...item,
          collapsed: true,
          options: item.options
            ? item.options.find((x) => x.selected)
              ? item.options
              : item.options.map((option) => ({ ...option, selected: false }))
            : [],
        }))
      );
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleReset = () => {
    setIsReset(true);
    if (dialogFilters)
      setFilters(
        dialogFilters?.map((item) => ({
          ...item,
          collapsed: true,
          options: item.options ? item.options.map((option) => ({ ...option, selected: false })) : [],
        }))
      );
  };

  const handleApply = () => {
    onApply(filters);
    setIsReset(false);
    handleClose();
  };

  const showMoreHandler = (label: string) => {
    setFilters((prev) => {
      const updatedFilters = [...prev];

      const id = updatedFilters.findIndex((x) => x.label === label);
      if (id !== -1) {
        updatedFilters[id] = {
          ...updatedFilters[id],
          showMore: !updatedFilters[id].showMore,
        };
      }

      return updatedFilters;
    });
  };

  const toggleFilter = (label: string) => {
    setFilters((prev) => {
      const updatedFilters = [...prev];

      const id = updatedFilters.findIndex((x) => x.label === label);
      if (id !== -1) {
        updatedFilters[id] = {
          ...updatedFilters[id],
          collapsed: !updatedFilters[id].collapsed,
        };
      }

      return updatedFilters;
    });
  };

  const onChooseRadioOption = (label: string, option: string) => {
    setFilters((prev) => {
      return prev.map((filter) => {
        if (filter.label !== label || !filter.options) return filter;

        const updatedOptions = filter.options.map((opt) => ({
          ...opt,
          selected: !opt.selected && opt.label === option,
        }));

        return { ...filter, options: updatedOptions };
      });
    });
  };

  const onChooseOption = (label: string, option: string) => {
    setFilters((prev) => {
      return prev.map((filter) => {
        if (filter.label !== label || !filter.options) return filter;

        const updatedOptions = filter.options.map((opt) =>
          opt.label === option ? { ...opt, selected: !opt.selected } : opt
        );

        return { ...filter, options: updatedOptions };
      });
    });
  };

  const filtersApplied = useMemo(() => filters.filter((x) => x.options?.some((o) => o.selected)).length, [filters]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      placement="side"
      size="sm"
      title="Filters"
      subtitle={
        <span className={`filter-dialog__status${filtersApplied > 0 ? " is-applied" : ""}`}>
          {filtersApplied > 0 ? filtersApplied : "None"} applied
        </span>
      }
      bodyClassName="filter-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleApply}>
            Confirm
          </Button>
        </>
      }
    >
      {filters.map((item, index) => {
        const filtersNum = item.options?.filter((o) => o.selected);
        const count = filtersNum && filtersNum.length > 0 ? <Tag tone="solid-spark">{filtersNum.length}</Tag> : null;

        return (
          <section className="filter-dialog__section" key={index}>
            {item.checkbox ? (
              // A filter that is switched on as a whole: the box opens its options
              <div className="filter-dialog__head">
                <span className="filter-dialog__label">{item.label}</span>
                <span className="filter-dialog__head-end">
                  {count}
                  <Checkbox
                    checked={!item.collapsed}
                    onChange={() => toggleFilter(item.label)}
                    label={item.label}
                    hideLabel
                  />
                </span>
              </div>
            ) : (
              <button
                type="button"
                className="filter-dialog__head"
                aria-expanded={!item.collapsed}
                onClick={() => toggleFilter(item.label)}
              >
                <span className="filter-dialog__label">{item.label}</span>
                <span className="filter-dialog__head-end">
                  {count}
                  {item.collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </span>
              </button>
            )}

            {!item.collapsed && (
              <div className="filter-dialog__options">
                {item.options
                  ?.filter((option, id) => (item.showMore ? true : id < 5))
                  .map((option, id) =>
                    item.radio ? (
                      // A radio that can be cleared again: pressing the chosen option unselects it
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={!!option.selected}
                        className={`filter-dialog__radio${option.selected ? " is-on" : ""}`}
                        onClick={() => onChooseRadioOption(item.label, option.label)}
                      >
                        <span className="filter-dialog__radio-dot" aria-hidden="true" />
                        {option.label}
                      </button>
                    ) : (
                      <Checkbox
                        key={id}
                        checked={!!option.selected}
                        onChange={() => onChooseOption(item.label, option.label)}
                        label={option.label}
                      />
                    )
                  )}
                {item.options && item.options?.length > 5 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="filter-dialog__more"
                    icon={!item.showMore ? <Plus size={18} /> : <Minus size={18} />}
                    onClick={() => showMoreHandler(item.label)}
                  >
                    Show {item.showMore ? "less" : "more"}
                  </Button>
                )}
              </div>
            )}
          </section>
        );
      })}
    </Dialog>
  );
};

export default FilterDialog;
